import {memo, useCallback, useEffect, useRef} from 'react'
import {useTranslation} from '@/translations/i18n'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Text} from '@/components/ui/text/Text'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {AlertCircleIcon, FileSearch02Icon} from '@/components/ui/icon/Icon'
import {ScrollArea} from '@/components/ui/scroll-area/ScrollArea'
import {Scrollbar} from '@/components/ui/scrollbar/Scrollbar'
import {ExportsListParams} from '@/features/exports/types'
import {useExports} from '@/features/exports/query/useExports'
import {useDownloadExport} from '@/features/exports/query/useDownloadExport'
import {useRetryExport} from '@/features/exports/query/useRetryExport'
import {useCancelExport} from '@/features/exports/query/useCancelExport'
import {useArchiveExport} from '@/features/exports/query/useArchiveExport'
import {useDeleteExport} from '@/features/exports/query/useDeleteExport'
import {ExportRow} from '@/features/exports/components/export-row/ExportRow'
import {StyledExportsEmptyState, StyledExportsList} from './style'

interface ExportsListProps {
    params: Omit<ExportsListParams, 'page'>
    /** Show the entity (type) label as each row's title. */
    showEntity?: boolean
}

const ExportsListComponent = ({params, showEntity = false}: ExportsListProps) => {
    const {t} = useTranslation()
    const endOfListRef = useRef<HTMLDivElement>(null)

    const {jobs, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage} = useExports(params)
    const {download, downloadingId} = useDownloadExport()
    const retryMutation = useRetryExport()
    const cancelMutation = useCancelExport()
    const archiveMutation = useArchiveExport()
    const deleteMutation = useDeleteExport()

    const handleObserver = useCallback(
        (entries: IntersectionObserverEntry[]) => {
            const [entry] = entries
            if (entry.isIntersecting && hasNextPage && !isLoading && !isFetchingNextPage) {
                fetchNextPage()
            }
        },
        [hasNextPage, isLoading, isFetchingNextPage, fetchNextPage]
    )

    useEffect(() => {
        if (!hasNextPage || !endOfListRef.current) return
        const observer = new IntersectionObserver(handleObserver, {root: null, rootMargin: '0px', threshold: 0.5})
        const node = endOfListRef.current
        observer.observe(node)
        return () => observer.unobserve(node)
    }, [handleObserver, hasNextPage, jobs.length])

    if (isLoading && !jobs.length) {
        return (
            <Flexbox justify="center" align="center" style={{height: '100%'}}>
                <Spinner size={24} />
            </Flexbox>
        )
    }

    if (isError) {
        return (
            <StyledExportsEmptyState direction="column" align="center" justify="center" gap={2}>
                <AlertCircleIcon size={24} />
                <Text size="textMd" palette="neutral" shade="600">
                    {t('exports:error')}
                </Text>
                <Text size="textSm" palette="neutral" shade="500">
                    {t('exports:error_loading')}
                </Text>
            </StyledExportsEmptyState>
        )
    }

    if (!jobs.length) {
        return (
            <StyledExportsEmptyState direction="column" align="center" justify="center" gap={2}>
                <FileSearch02Icon size={24} />
                <Text size="textMd" palette="neutral" shade="600">
                    {t('exports:empty_title')}
                </Text>
                <Text size="textSm" palette="neutral" shade="500">
                    {t('exports:empty_subtitle')}
                </Text>
            </StyledExportsEmptyState>
        )
    }

    return (
        <ScrollArea style={{height: '100%'}} scrollbar={<Scrollbar />}>
            <StyledExportsList direction="column">
                {jobs.map(job => (
                    <ExportRow
                        key={job.id}
                        job={job}
                        showEntity={showEntity}
                        isDownloading={downloadingId === job.id}
                        isRetrying={retryMutation.isPending && retryMutation.variables === job.id}
                        isCancelling={cancelMutation.isPending && cancelMutation.variables === job.id}
                        isArchiving={archiveMutation.isPending && archiveMutation.variables === job.id}
                        isDeleting={deleteMutation.isPending && deleteMutation.variables === job.id}
                        onDownload={download}
                        onRetry={retryMutation.mutate}
                        onCancel={cancelMutation.mutate}
                        onArchive={archiveMutation.mutate}
                        onDelete={deleteMutation.mutate}
                    />
                ))}

                {(hasNextPage || isFetchingNextPage) && (
                    <div ref={endOfListRef} style={{height: '20px', padding: '10px 0'}}>
                        {isFetchingNextPage && (
                            <Flexbox justify="center" align="center">
                                <Spinner size={20} />
                            </Flexbox>
                        )}
                    </div>
                )}
            </StyledExportsList>
        </ScrollArea>
    )
}

export const ExportsList = memo(ExportsListComponent)
